import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import BottomNav from '../components/BottomNav';
import { MonthlyMoodRecap } from '../components/MonthlyMoodRecap';
import { useAuth } from '../context/AuthContext';

const Seed = ({ color, size = 10, className, style }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" className={`absolute pointer-events-none ${className}`} style={style}>
    <line x1="10" y1="16" x2="10" y2="7" stroke={color} strokeWidth="1" strokeLinecap="round" />
    <circle cx="10" cy="6" r="1.4" fill={color} />
    <line x1="10" y1="6" x2="5" y2="1" stroke={color} strokeWidth="0.7" opacity="0.8" />
    <line x1="10" y1="6" x2="15" y2="1" stroke={color} strokeWidth="0.7" opacity="0.8" />
    <line x1="10" y1="6" x2="10" y2="0" stroke={color} strokeWidth="0.7" opacity="0.8" />
  </svg>
);

const Menu = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Premier prénom seulement, capitalisé — jamais d'email, de "undefined" ou d'espace
  const rawName = typeof user?.name === 'string' ? user.name.trim() : '';
  const rawFirst = rawName && !rawName.includes('@') ? rawName.split(/\s+/)[0] : '';
  const firstName = rawFirst ? rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1) : '';

  const themes = [
    {
      id: 'meditation',
      title: 'Méd',
      titleAccent: 'i',
      titleEnd: 'tation Guidée',
      subtitle: "L'art de s'abandonner à Sa présence",
      icon: '/logo-theme-meditation.png',
      accent: '#F79AAE',
      path: '/meditation'
    },
    {
      id: 'dhikr',
      title: 'Dh',
      titleAccent: 'i',
      titleEnd: 'kr',
      subtitle: "L'art de semer des graines d'Amour",
      icon: '/logo-theme-dhikr.png',
      accent: '#9CCB8F',
      path: '/dhikr'
    },
    {
      id: 'respiration',
      title: 'Resp',
      titleAccent: 'i',
      titleEnd: 'ration',
      subtitle: "L'art de se relier par le souffle",
      icon: '/logo-theme-respiration.png',
      accent: '#A8D8EA',
      path: '/respiration'
    }
  ];

  return (
    <div className="lato-screen min-h-screen bg-[#FAF9F6] flex flex-col pb-24 relative overflow-hidden">
      <MonthlyMoodRecap />
      {/* Header with greeting — remonté légèrement pour équilibrer l'écran */}
      <div className="pt-7 px-5">
        <div className="max-w-md mx-auto">
          <div className="flex flex-col items-center">
            <span
              className="font-script"
              style={{ fontSize: '28px', fontWeight: 500, color: '#1a1a1a' }}
              data-testid="greeting-salam"
            >
              As-salāmu ʿalaykum{firstName ? ',' : ''}
            </span>
            {firstName && (
              <span
                className="font-script mt-1"
                style={{ fontWeight: 400, fontSize: '26px', color: '#F79AAE', maxWidth: '100%', textAlign: 'center', overflowWrap: 'break-word' }}
                data-testid="greeting-firstname"
              >
                {firstName}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Cards Container — centré verticalement, marges verticales uniformes */}
      <div className="flex-1 px-6 flex items-center justify-center">
        <div className="w-full max-w-md mx-auto space-y-6 py-6">
          {themes.map((theme, index) => {
            return (
              <button
                key={theme.id}
                onClick={() => navigate(theme.path)}
                className="w-full animate-fade-in focus:outline-none group"
                data-testid={`menu-card-${theme.id}`}
              >
                <Card className="relative bg-[#FFFDF6] border border-[#F3EEE5] rounded-[28px] group-hover:shadow-md group-active:-translate-y-[2px] group-active:shadow-[0_6px_16px_rgba(0,0,0,0.08)] group-active:border-[#DFB3AD] shadow-[0_2px_12px_rgba(0,0,0,0.04)] transition-all duration-150 overflow-hidden">
                  <div className="p-5 h-[120px] flex items-center gap-5 text-left">
                    {/* Theme icon — le logo officiel découpé en calques : les éléments animés SONT les éléments du logo */}
                    <div className="w-20 h-20 relative flex-shrink-0 flex items-center justify-center" style={{ overflow: 'visible' }}>
                      {theme.id === 'meditation' ? (
                        /* Méditation — 3 vraies aigrettes du logo se détachent, montent en courbe douce, reviennent. Le reste est immobile. */
                        <div className="relative w-full h-full">
                          <img src="/logo-med-base.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain" />
                          <img src="/logo-med-part1.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain med-real-1" />
                          <img src="/logo-med-part2.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain med-real-2" />
                          <img src="/logo-med-part3.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain med-real-3" />
                        </div>
                      ) : theme.id === 'dhikr' ? (
                        /* Dhikr — une onde traverse les vraies tiges du logo l'une après l'autre (1-2px). Rien d'autre ne bouge. */
                        <div className="relative w-full h-full">
                          <img src="/logo-dhikr-base.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain" />
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                            <img
                              key={i}
                              src={`/logo-dhikr-part${i}.png?v=4`}
                              alt=""
                              className="absolute inset-0 w-full h-full object-contain dhikr-real"
                              style={{ animationDelay: `${((i - 1) * (8 / 9)).toFixed(2)}s` }}
                            />
                          ))}
                        </div>
                      ) : (
                        /* Respiration — la bulle reste immobile ; seul le pissenlit bouge doucement et subtilement */
                        <div className="relative w-full h-full">
                          <img src="/logo-resp-base.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain" />
                          <img src="/logo-resp-pissenlit.png?v=4" alt="" className="absolute inset-0 w-full h-full object-contain resp-sway" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3
                        className="text-gray-900 leading-[1.15]"
                        style={{ fontFamily: "'Manrope', sans-serif", fontSize: '22px', fontWeight: 600 }}
                      >
                        {theme.title}<span style={{ color: theme.accent }}>{theme.titleAccent}</span>{theme.titleEnd}
                      </h3>
                      <p
                        className="text-gray-500 mt-2 leading-[1.4]"
                        style={{ fontSize: '14px' }}
                      >
                        {theme.subtitle}
                      </p>
                    </div>
                  </div>
                </Card>
              </button>
            );
          })}
        </div>
      </div>
      
      <style>{`
        /* Méditation guidée — vraies aigrettes du logo (calques). Détachement lent, courbe naturelle,
           fondu progressif, retour exact à l'origine. Boucle parfaite 4.5s. Ni rotation, ni zoom, ni couleur. */
        .med-real-1 { animation: med-real-a 4.5s ease-in-out infinite; }
        .med-real-2 { animation: med-real-b 4.5s ease-in-out 1.5s infinite; }
        .med-real-3 { animation: med-real-a 4.5s ease-in-out 3s infinite; }
        @keyframes med-real-a {
          0%    { transform: translate(0, 0); opacity: 1; }
          28%   { transform: translate(2px, -4px); opacity: 0.7; }
          55%   { transform: translate(5px, -9px); opacity: 0; }
          55.1% { transform: translate(0, 0); opacity: 0; }
          82%   { opacity: 0; }
          100%  { transform: translate(0, 0); opacity: 1; }
        }
        @keyframes med-real-b {
          0%    { transform: translate(0, 0); opacity: 1; }
          28%   { transform: translate(3.2px, -3.2px); opacity: 0.7; }
          55%   { transform: translate(7.5px, -7px); opacity: 0; }
          55.1% { transform: translate(0, 0); opacity: 0; }
          82%   { opacity: 0; }
          100%  { transform: translate(0, 0); opacity: 1; }
        }

        /* Dhikr — onde organique très lente : chaque vraie tige du logo se redresse de 1.5px puis revient,
           l'une après l'autre. Boucle parfaite 8s. Aucun halo, aucune lumière, aucune particule. */
        .dhikr-real { animation: dhikr-real 8s ease-in-out infinite; }
        @keyframes dhikr-real {
          0%, 100% { transform: translateY(0); }
          10%      { transform: translateY(-2.2px); }
          20%      { transform: translateY(0); }
        }

        /* Respiration — la bulle est immobile ; seul le pissenlit (calque réel) penche très doucement, puis revient */
        .resp-sway {
          transform-origin: 40% 96%;
          animation: resp-sway 8s ease-in-out infinite;
        }
        @keyframes resp-sway {
          0%, 100% { transform: rotate(0deg); }
          45%      { transform: rotate(3deg); }
          60%      { transform: rotate(2.3deg); }
        }

        @keyframes seed-float {
          0%, 100% { transform: translateY(0) translateX(0) rotate(0deg); }
          50% { transform: translateY(-8px) translateX(4px) rotate(4deg); }
        }
        .seed-float-1 { animation: seed-float 6s ease-in-out infinite; }
        .seed-float-2 { animation: seed-float 7.5s ease-in-out infinite 1s; }
        .seed-float-3 { animation: seed-float 6.8s ease-in-out infinite 2s; }
        .seed-float-4 { animation: seed-float 8s ease-in-out infinite 0.5s; }

        @keyframes fade-in {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes wave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-20deg); }
          75% { transform: rotate(20deg); }
        }
        
        @keyframes crescent-glow {
          0%, 100% { opacity: 1; filter: drop-shadow(0 0 2px rgba(193, 123, 117,0.3)); }
          50% { opacity: 0.85; filter: drop-shadow(0 0 6px rgba(193, 123, 117,0.5)); }
        }
        
        @keyframes star-twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(0.8); }
        }
        
        @keyframes star-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-2px); }
        }
        
        @keyframes tasbih-rotate {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes bead-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.8; }
        }
        
        @keyframes breath-expand {
          0%, 100% { transform: scale(0.9); opacity: 0.6; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        
        @keyframes breath-core {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.15s ease-out both;
        }
        
        .salam-wave {
          transform-origin: 70% 90%;
          animation: salam-wave 0.7s ease-in-out 5;
        }
        
        @keyframes salam-wave {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-7deg); }
          75% { transform: rotate(7deg); }
        }
        
        .icon-crescent {
          animation: crescent-glow 5s ease-in-out infinite;
        }
        
        .icon-star {
          animation: star-twinkle 4.5s ease-in-out infinite;
          transform-origin: center;
        }
        
        .icon-star-black {
          animation: star-float 5s ease-in-out infinite;
        }
        
        .icon-star-tiny {
          animation: star-twinkle 6s ease-in-out infinite 1.2s;
          transform-origin: center;
        }
        
        .icon-tasbih-ring {
          animation: tasbih-rotate 40s linear infinite;
          transform-origin: 40px 36px;
        }
        
        .tasbih-bead {
          animation: bead-pulse 5s ease-in-out infinite;
        }
        
        .tasbih-bead:nth-child(2) { animation-delay: 0.55s; }
        .tasbih-bead:nth-child(3) { animation-delay: 1.1s; }
        .tasbih-bead:nth-child(4) { animation-delay: 1.65s; }
        .tasbih-bead:nth-child(5) { animation-delay: 2.2s; }
        .tasbih-bead:nth-child(6) { animation-delay: 2.75s; }
        .tasbih-bead:nth-child(7) { animation-delay: 3.3s; }
        .tasbih-bead:nth-child(8) { animation-delay: 3.85s; }
        .tasbih-bead:nth-child(9) { animation-delay: 4.4s; }
        
        .tasbih-tassel-g {
          animation: tassel-sway 7s ease-in-out infinite;
          transform-origin: 40px 56px;
        }
        
        @keyframes tassel-sway {
          0%, 100% { transform: rotate(-4deg); }
          50% { transform: rotate(4deg); }
        }
        
        .breath-ring1 {
          animation: breath-expand 4s ease-in-out infinite;
          transform-origin: center;
        }
        
        .breath-ring2 {
          animation: breath-expand 4s ease-in-out infinite 0.5s;
          transform-origin: center;
        }
        
        .breath-core {
          animation: breath-core 4s ease-in-out infinite;
          transform-origin: center;
        }
      `}</style>
      
      <BottomNav />
    </div>
  );
};

export default Menu;
